import { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

export default function Topbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const navigate = useNavigate();

  const handlePasswordUpdate = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token JWT manquant");

      await axios.post(
        "https://staging-third-party-apis.nabil5332.workers.dev/api/v1/auth/update-password",
        { oldPassword, newPassword },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess("Mot de passe mis à jour avec succès !");
      setOldPassword("");
      setNewPassword("");
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        console.error("❌ Erreur update password:", err.response?.data || err.message);
        setError(
          (err.response?.data as { error?: string })?.error ||
          "Erreur lors de la mise à jour."
        );
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur inconnue est survenue.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("apiKey");
    navigate("/");
  };

  return (
    <>
      <header
        style={{
          height: "60px",
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "0 1rem",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <button
          style={{
            backgroundColor: "#714291",
            color: "white",
            border: "none",
            borderRadius: "0.5rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
          onClick={() => setDrawerOpen(true)}
        >
          ⚙️ Paramètres
        </button>
      </header>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 350, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Paramètres</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="subtitle1" mb={1}>
            🔒 Changer le mot de passe
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Stack spacing={2} mb={3}>
            <TextField
              label="Ancien mot de passe"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Nouveau mot de passe"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handlePasswordUpdate}
              disabled={loading || !oldPassword || !newPassword}
            >
              {loading ? <CircularProgress size={24} /> : "Mettre à jour"}
            </Button>
          </Stack>

          <Button
            variant="outlined"
            color="error"
            onClick={handleLogout}
            fullWidth
          >
            🚪 Se déconnecter
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
